import React from 'react'
import { useSelector } from 'react-redux'
import { Box, CircularProgress } from '@mui/material'
import { useHasPermissions } from '@hooks/useHasPermission'
import { useEnabledFeatures, FeatureName } from '@hooks/useFeature'
import { PermissionITF } from '@/authProvider'
import { RootStateITF } from '@store/redux-types'
import AccessDenied from './AccessDenied'

interface ProtectedRouteProps {
  permissions?: (keyof PermissionITF | string)[]
  mode?: 'any' | 'all'
  /**
   * Intelligence modules this route belongs to; the route is denied unless the
   * portfolio has at least one of them. Closes direct URLs to a dashboard whose
   * module was turned off — the backend's feature gate is the real boundary.
   */
  features?: FeatureName[]
  deniedMessage?: string
  children: React.ReactElement
}

const loaderSx = { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }
const loader = (
  <Box sx={loaderSx}>
    <CircularProgress />
  </Box>
)

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  permissions,
  mode = 'any',
  features,
  deniedMessage,
  children,
}) => {
  // null = not hydrated yet (fresh tab recovering via CheckIn) — distinct from
  // a loaded set that lacks the required key. Denying on the former flashed
  // Access Denied over a state that was still loading.
  const permissionsLoaded = useSelector((state: RootStateITF) => !!state.auth.permissions)
  const hasAccess = useHasPermissions(permissions || [], mode)
  const enabledFeatures = useEnabledFeatures()

  if (permissions?.length && !permissionsLoaded) {
    return loader
  }

  if (permissions?.length && !hasAccess) {
    return <AccessDenied message={deniedMessage} />
  }

  // Same hydration caveat as permissions: an empty flag map means the session
  // context has not landed yet, so wait rather than flashing Access Denied.
  if (features?.length) {
    if (!Object.keys(enabledFeatures).length) {
      return loader
    }
    if (!features.some((feature) => enabledFeatures[feature])) {
      return <AccessDenied message={deniedMessage} />
    }
  }

  return children
}

export default React.memo(ProtectedRoute)
